/** @jsxImportSource @emotion/react */

import { useAtom } from 'jotai';
import useNotifications from '../../../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../../../login/v1/utility';
import useAddonPurchase from '../../../../shared/hooks/use-addon-purchase';
import { useCallback } from 'react';
import { openPopup } from '../../../../shared/utils/popup';
import QSModal from '../../../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../../../shared/components/atoms';
import ProgressiveImage from '../../../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../../../renew-plan/v1/typings';
import { Close } from '@mui/icons-material';
import { ReactComponent as CallIncomingIcon } from '../../../../shared/assets/svg/call-incoming.svg';
import { ReactComponent as SwitchIcon } from '../../../../shared/assets/svg/switch.svg';
import { ReactComponent as CallRecordingIcon } from '../../../../shared/assets/svg/call-recording.svg';
import Loader from '../../../../shared/components/molecules/loader';
import WaVoiceImage from '../../images/wa-voice-image.png';
import { waVoiceUpgradeModalOpen } from '../states';
import PhoneOutlined from '../../../../shared/icons/PhoneOutlined';

const FEATURES = [
  {
    featureLabel: 'Multiple incoming and outgoing calls',
    desc: 'Monitor multiple incoming and outgoing calls simultaneously',
    icon: CallIncomingIcon,
    iconColor: '#3D906D',
  },
  {
    featureLabel: 'Call recordings',
    desc: 'Automatically record and download all calls for easy review and analysis',
    icon: CallRecordingIcon,
    iconColor: '#EF5350',
  },
  {
    featureLabel: 'Call logs',
    desc: 'Keep track of all your calls. Easily review and analyse your call history',
    icon: SwitchIcon,
    iconColor: '#596BD7',
  },
];

export default function WaVoiceUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(waVoiceUpgradeModalOpen);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();
  let contactUsLink = 'https://wa.me/918356849474?text=I%27m%20interested%20in%20WhatsApp%20Calls';
  if (hideDTDetails) {
    contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20WhatsApp%20Calls`;
  }

  const onClose = useCallback(() => {
    setUpgradeModal(false);
  }, [setUpgradeModal]);

  const onClickContactUs = useCallback(async () => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
      } else {
        await purchaseAddon(PlanAddonTypes.WaVoiceAddon);
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal) {
    return null;
  }

  const channelName = channelData?.name || 'your workspace';

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
              width: 320px;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '374px' }} src={WaVoiceImage} />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 0;
              padding-left: 24px;
              max-width: 356px;
            `}
          >
            <span
              css={css`
                border-radius: 4px;
                padding: 6px 8px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                font-weight: 600;
                color: #596bd7;
                background-color: #596bd714;
              `}
            >
              <PhoneOutlined width={16} height={16} color="#596bd7" />
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                WhatsApp Calls
              </span>
            </span>
            <QSTypography
              css={css`
                font-weight: 600;
                padding-right: 12px;
                font-size: 20px;
                overflow: hidden;
                line-height: 28px;
                margin-bottom: 16px;
                margin-top: 8px;
              `}
            >
              Seamless WhatsApp Calls on {hideDTDetails ? channelName : 'DoubleTick'} with Recordings and Call history
            </QSTypography>

            {FEATURES.map((feature, key) => {
              const Icon = feature?.icon;
              return (
                <div
                  key={key}
                  css={css`
                    display: inline-flex;
                    align-items: flex-start;
                    justify-content: flex-start;
                    margin-bottom: 10px;
                    font-weight: 500;
                    gap: 12px;
                  `}
                >
                  <div
                    style={{
                      height: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: feature.iconColor ?? '#2d2d2d',
                    }}
                  >
                    <Icon width={24} height={24} />
                  </div>
                  <span
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 4px;
                    `}
                  >
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 16.94px;
                        text-align: left;
                        color: #2d2d2d;
                      `}
                    >
                      {feature?.featureLabel}
                    </div>
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 12px;
                        font-weight: 500;
                        line-height: 14.52px;
                        color: #7a7a7a;
                      `}
                    >
                      {feature?.desc}
                    </div>
                  </span>
                </div>
              );
            })}
            <div
              css={css`
                margin-top: 8px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 10px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                `}
                onClick={onClickContactUs}
              >
                {hideDTDetails ? (
                  'Contact Us'
                ) : (
                  <>
                    {purchaseLoading ? (
                      <Loader
                        size={16}
                        loaderProps={{
                          sx: {
                            color: 'white',
                          },
                        }}
                      />
                    ) : (
                      'Purchase Now'
                    )}
                  </>
                )}
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
}
